/** Every figure the product prints is formatted here, and nowhere else.
 *
 *  `Intl` and `toLocaleString` fall back to the reader's browser language when
 *  no locale is named, so the same number came out "USD 401.641" on an English
 *  machine and "USD 401,641" on a Spanish one — and the Spanish reading turned
 *  401 dollars into 401 thousand. Grouping is a property of the product, not of
 *  whoever opens it, so every formatter below names `en-US` explicitly.
 *
 *  Components do not call `toLocaleString` or `Intl.*` directly. Add a helper
 *  here instead: four near-identical money formatters had already drifted apart
 *  across the front before they were merged into this file. */

/** Amounts read "USD 123,120,492", not "$123,120,492".
 *
 *  The code, not the symbol: the platform reports in several currencies and a
 *  bare $ is ambiguous across them. It also reads better at display size — the
 *  $ glyph collides with the first digit where a three-letter code sits apart.
 *
 *  For headline figures use `usdCompact`; nine digits is not a number anyone
 *  reads, it is a number they measure. */
export function usd(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "code",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** "USD 123.1M". Headline amounts, where magnitude is the message and the
 *  hundreds place is noise. Pair it with `usd()` in a title attribute when the
 *  exact figure still has to be reachable. */
export function usdCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "code",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** "3.8M". Counts at headline size, same reasoning as `usdCompact`. */
export function numCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function pct(value: number | null | undefined, decimals = 1): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(decimals)}%`;
}

export function num(value: number | null | undefined, decimals = 2): string {
  if (value == null) return "—";
  return value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** "4,730". Counts — loans, debtors, rows, installments. The workhorse: a count
 *  never carries decimals, and a grouped one is the only readable form past
 *  four digits. */
export function int(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** "1.2", "0.9583" — up to `decimals` places, trailing zeros dropped. For
 *  figures where the precision is available but not always meaningful, such as
 *  an FX rate. `num()` is the fixed-width counterpart. */
export function numMax(value: number | null | undefined, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: decimals });
}

/** "USD 1.9M", "USD 402K", "USD 401.64". The abbreviated money format, used by
 *  every card and table that reports a balance.
 *
 *  The thresholds are not uniform on purpose: between 1K and 10K one decimal
 *  still carries information ("USD 1.5K"), above it the hundreds place is noise
 *  ("USD 402K"). Below a thousand the figure is printed whole, and that branch
 *  is the one that has to name a locale — the others build their string with
 *  `toFixed`, which is locale-independent. Left to the machine it printed "USD
 *  401,641" for 401.64 next to a "USD 1.9M" that never moved.
 *
 *  The sign goes before the currency code, so a negative reads "-USD 2.5K". */
export function fmtAmt(v: number | null | undefined, currency: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}${currency} ${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${currency} ${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 10e3) return `${sign}${currency} ${(abs / 1e3).toFixed(0)}K`;
  if (abs >= 1e3) return `${sign}${currency} ${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${currency} ${abs.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/** "PEN 2,606,180.21". Exact, not abbreviated: these are the figures a reader
 *  checks against the facility cockpits, so the cents have to be there. Null is
 *  a dash — the caller says why next to it. */
export function money(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // `currencyDisplay: "code"` separates the code from the figure with a
      // non-breaking space. It looks identical to a space and does not survive a
      // copy into a spreadsheet, so the separator is normalised below; it is the
      // only whitespace the formatter emits.
    }).format(value).replace(/\s/g, " ");
  } catch {
    // An unknown or malformed ISO code makes `Intl` throw. The figure still has
    // to reach the screen, so it is grouped by hand and the code prefixed.
    return `${currency} ${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/** "USD 401,641". Exact, whole units, currency travelling with the figure. The
 *  tooltip form of `fmtAmt`: the abbreviated number is what a reader scans, this
 *  is what they check. */
export function exactNative(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${currency} ${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/** "USD 402K". Compact money in a native currency, for rows that carry several
 *  figures and cannot afford full precision on any of them. */
export function nativeAmount(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${currency} ${COMPACT_NUM.format(value)}`;
}

/** Compact notation: a row carries several figures and full precision on all of
 *  them is unreadable. The exact number stays one hover away.
 *
 *  Currency code rather than the $ symbol, matching `usd()` above. */
export const COMPACT_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "code",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const COMPACT_NUM = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/* ── Dates ──────────────────────────────────────────────────────────────
 *
 *  Month and weekday names come from a table, not from `Intl`.
 *
 *  `Intl` resolves them through CLDR, whose data differs by locale and changes
 *  between releases: `en-GB` abbreviates September as "Sept" — four letters,
 *  the only month that does — where `en-US` says "Sep". The product printed
 *  both, one screen apart, and only in September. Eleven months a year the
 *  drift was invisible, which is why it survived.
 *
 *  A table makes the wording ours: no locale left to get wrong, and a CLDR
 *  revision cannot restyle the product. One shape per need, day first and 24h
 *  throughout, because half the surfaces already read that way and a reader
 *  comparing two screens should not have to notice which one they are on.
 *
 *  Absolute always, never relative. There is no "3 weeks ago" here and none
 *  should be added: a book dated that way is a book nobody can reconcile
 *  against a statement. `timeRemaining` is the one exception and it sits beside
 *  the absolute deadline, never instead of it. */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad2 = (n: number) => String(n).padStart(2, "0");

export type DateLike = string | Date | null | undefined;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A date-only string names a calendar day, not an instant. Parsed as an
 *  instant it lands on UTC midnight, and a reader west of Greenwich then sees
 *  the day before — a snapshot dated the 1st reading as the last of the prior
 *  month. Those are read off the string itself; a full timestamp is read in
 *  local time, which is what "when did this run" means to whoever asks. */
function parts(v: DateLike) {
  if (v == null || v === "") return null;
  if (typeof v === "string" && DATE_ONLY.test(v)) {
    const [y, m, d] = v.split("-").map(Number);
    return { y, m: m - 1, d, hh: 0, mi: 0, wd: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
  }
  const t = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(t.getTime())) return null;
  return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate(), hh: t.getHours(), mi: t.getMinutes(), wd: t.getDay() };
}

/** "24 Sep 2026". The default for any date a reader has to check. */
export function formatDate(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${p.y}` : "—";
}

/** "24 Sep 2026 16:32". Absolute timestamp, always with the clock. */
export function formatDateTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${p.y} ${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** "24 Sep". Inside a column or an axis, where the year is already given. */
export function formatDayMonth(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]}` : "—";
}

/** "24 Sep 16:32". A recent event, where the year would be noise. */
export function formatDayMonthTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** "Thu, 24 Sep". Chart tooltips, where the weekday explains a weekly shape. */
export function formatWeekdayDayMonth(v: DateLike): string {
  const p = parts(v);
  return p ? `${WEEKDAYS[p.wd]}, ${pad2(p.d)} ${MONTHS[p.m]}` : "—";
}

/** "Sep 2026". A reporting period, which has no day. */
export function formatMonthYear(v: DateLike): string {
  const p = parts(v);
  return p ? `${MONTHS[p.m]} ${p.y}` : "—";
}

/** "Sep ’26". A monthly axis, where every tick repeats the century. */
export function formatMonthYearShort(v: DateLike): string {
  const p = parts(v);
  return p ? `${MONTHS[p.m]} ’${pad2(p.y % 100)}` : "—";
}

/** "US Dollar" for "USD". The spelled-out name of a currency code, for the few
 *  places that introduce a currency rather than quote one. Pinned to English
 *  like every other string the product prints. Returns null for a code `Intl`
 *  does not know, which the caller says in its own words. */
const CURRENCY_NAMES = new Intl.DisplayNames(["en"], { type: "currency" });

export function currencyName(code: string): string | null {
  try {
    const name = CURRENCY_NAMES.of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** "16:32". The clock alone, next to a date already on screen. */
export function formatTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** Time left until a deadline — "2d 4h", "4h 12m", "35m".
 *  Returns null when the deadline has passed (caller renders OVERDUE)
 *  or when there is no valid deadline. Shown next to the absolute
 *  timestamp, never instead of it. */
export function timeRemaining(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const deadline = new Date(iso);
  if (isNaN(deadline.getTime())) return null;
  const ms = deadline.getTime() - now.getTime();
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export function statusColor(status: string | null | undefined): string {
  switch (status?.toUpperCase()) {
    case "COMPLIANT": return "status-compliant";
    case "WARNING": return "status-warning";
    case "BREACHED": return "status-breached";
    default: return "bg-gray-100 text-gray-600";
  }
}
